import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Send, MapPin, FileText, CheckCircle, Loader2 } from 'lucide-react';
import { companyInfo } from '../data/mock';

function careersApplyUrl() {
  const fromEnv = process.env.REACT_APP_CAREERS_API_URL;
  if (fromEnv) {
    return `${fromEnv.replace(/\/$/, '')}/api/careers/apply`;
  }
  if (process.env.NODE_ENV === 'development') {
    return '/api/careers/apply';
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/_/backend/api/careers/apply`;
  }
  return '';
}

export const Careers = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const url = careersApplyUrl();
    if (!url) {
      setError('URL da API não disponível.');
      return;
    }

    const form = e.currentTarget;
    const fd = new FormData(form);

    setSubmitting(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          typeof data.detail === 'string'
            ? data.detail
            : Array.isArray(data.detail)
              ? data.detail.map((d) => d.msg || d).join(' ')
              : 'Não foi possível enviar a candidatura.';
        throw new Error(msg);
      }
      navigate('/recrutamento?sucesso=true');
    } catch (err) {
      setError(err.message || 'Erro de rede. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#003d82] to-[#004a9c] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Users size={64} className="mx-auto mb-6 text-[#f5a623]" />
            <h1 className="text-5xl font-bold mb-6">Junte-se à Nossa Equipa</h1>
            <p className="text-xl text-gray-200">
              Faça parte de uma empresa com mais de 20 anos de experiência no transporte rodoviário de passageiros
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003d82] mb-8 text-center">Porquê Trabalhar Connosco?</h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {[
                {
                  icon: CheckCircle,
                  title: 'Empresa Sólida',
                  description: 'Mais de 20 anos no mercado, com uma frota moderna de 24 autocarros',
                },
                {
                  icon: CheckCircle,
                  title: 'Ambiente Profissional',
                  description: 'Equipa experiente e ambiente de trabalho respeitador',
                },
                {
                  icon: CheckCircle,
                  title: 'Formação Contínua',
                  description: 'Investimos na formação e desenvolvimento dos nossos colaboradores',
                },
                {
                  icon: CheckCircle,
                  title: 'Estabilidade',
                  description: 'Oportunidades de carreira a longo prazo numa empresa estabelecida',
                },
              ].map((benefit, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-[#f5a623]/10 flex items-center justify-center">
                      <benefit.icon className="text-[#003d82]" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#003d82] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - How to Apply */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#003d82] mb-4">Como Se Candidatar</h2>
              <p className="text-xl text-gray-600">
                Se tem paixão pela condução e quer fazer parte da nossa equipa, candidate-se agora!
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Formulário de Candidatura */}
              <div className="bg-white rounded-xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#f5a623] flex items-center justify-center">
                    <Send className="text-[#003d82]" size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#003d82]">Formulário de Candidatura</h3>
                </div>

                <p className="text-sm text-gray-600 mb-4 rounded-lg bg-gray-50 border border-gray-200 p-3">
                  O CV é enviado pelo nosso servidor por email (SMTP). Não usa Web3Forms para anexos — evita plano
                  Pro. É necessário configurar SMTP no backend (ex.: Gmail com palavra-passe de aplicação).
                </p>

                <form onSubmit={onSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      autoComplete="name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d82] focus:border-transparent outline-none transition-all"
                      placeholder="O seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      autoComplete="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d82] focus:border-transparent outline-none transition-all"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      autoComplete="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d82] focus:border-transparent outline-none transition-all"
                      placeholder="912 345 678"
                    />
                  </div>

                  <div>
                    <label htmlFor="attachment" className="block text-sm font-semibold text-gray-700 mb-2">
                      Currículo (CV) *
                    </label>
                    <input
                      type="file"
                      id="attachment"
                      name="attachment"
                      required
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d82] focus:border-transparent outline-none transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#f5a623] file:text-[#003d82] hover:file:bg-[#e5a71b]"
                    />
                    <p className="text-xs text-gray-500 mt-1">PDF, DOC ou DOCX — máximo 8 MB.</p>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Mensagem / Experiência
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d82] focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Conte-nos sobre a sua experiência como motorista..."
                    />
                  </div>

                  {error ? (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#003d82] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#002a5c] transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 className="animate-spin" size={22} /> : <Send size={20} />}
                    {submitting ? 'A enviar…' : 'Enviar Candidatura'}
                  </button>

                  <p className="text-sm text-gray-500 text-center">* Campos obrigatórios</p>
                </form>
              </div>

              {/* Outras Opções */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all">
                  <div className="w-14 h-14 rounded-full bg-[#f5a623]/10 flex items-center justify-center mb-4">
                    <MapPin className="text-[#003d82]" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#003d82] mb-3">Presencialmente</h3>
                  <p className="text-gray-600 mb-4">Visite o nosso escritório e entregue o seu CV</p>
                  <p className="text-sm text-gray-600 mb-4">
                    {companyInfo.address.street}
                    <br />
                    {companyInfo.address.lojas}
                    <br />
                    {companyInfo.address.postal} {companyInfo.address.city}
                  </p>
                  <p className="text-sm font-semibold text-[#003d82]">
                    Horário: {companyInfo.hours.weekdays} (Segunda a Sexta)
                  </p>
                </div>

                <div className="bg-[#f5a623] text-[#003d82] rounded-xl p-6">
                  <h4 className="font-bold text-lg mb-2">💼 Candidaturas Espontâneas</h4>
                  <p className="text-sm">
                    Mesmo sem vagas abertas no momento, guardamos o seu CV para futuras oportunidades. Todas as
                    candidaturas são bem-vindas!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#f5a623]/10 border-l-4 border-[#f5a623] p-8 rounded-lg">
              <div className="flex items-start gap-4">
                <FileText className="text-[#003d82] flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-[#003d82] mb-4">Requisitos</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Carta de Condução D</li>
                    <li>• Certificado de Aptidão para Motorista (CAM)</li>
                    <li>• Cartão de Tacógrafo</li>
                    <li>• Experiência na condução de autocarros (preferencial)</li>
                    <li>• Disponibilidade para trabalhar em horários rotativos</li>
                    <li>• Sentido de responsabilidade e profissionalismo</li>
                    <li>• Boa capacidade de relacionamento interpessoal</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
